/**
 * Hero auto contrast
 *
 * Samples the part of the hero image that actually sits behind the hero's
 * text/buttons and picks pure black or pure white for them, whichever contrasts
 * with that area. The media overlay is tinted to match (dark backdrop keeps the
 * dark overlay, light backdrop gets a light one) so the text stays legible
 * either way.
 *
 * Opt in per section with data-hero-auto-contrast on the .hero element.
 */
(() => {
  const LIGHT_THRESHOLD = 0.45; // relative luminance above this counts as a light backdrop
  const SAMPLE_SIZE = 24; // canvas is downsampled to this before averaging
  const PROBE_WIDTH = 400; // width requested from the Shopify CDN for sampling
  const DEFAULT_OVERLAY_ALPHA = 0.6;

  /** sRGB channel (0-255) to linear light, per WCAG relative luminance. */
  const toLinear = (channel) => {
    const c = channel / 255;
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };

  const clamp01 = (n) => Math.min(1, Math.max(0, n));

  /** Ask the CDN for a small copy of the same image instead of decoding the full one. */
  function probeSrc(src) {
    try {
      const url = new URL(src, window.location.href);
      url.searchParams.set('width', String(PROBE_WIDTH));
      return url.toString();
    } catch {
      return src;
    }
  }

  /** Pull the alpha out of whatever colour format the overlay was rendered with. */
  function overlayAlpha(value) {
    const raw = (value || '').trim();
    if (!raw) return DEFAULT_OVERLAY_ALPHA;

    const hex8 = raw.match(/^#([0-9a-f]{6})([0-9a-f]{2})$/i);
    if (hex8) return parseInt(hex8[2], 16) / 255;
    if (/^#([0-9a-f]{6}|[0-9a-f]{3})$/i.test(raw)) return 1;

    const fn = raw.match(/rgba?\([^)]*?[,/]\s*([\d.]+)%?\s*\)/i);
    if (fn) {
      const alpha = parseFloat(fn[1]);
      return raw.includes('%') ? alpha / 100 : alpha;
    }
    return DEFAULT_OVERLAY_ALPHA;
  }

  /** Bounding box covering every element passed in, in viewport coordinates. */
  function unionRect(elements) {
    let left = Infinity;
    let top = Infinity;
    let right = -Infinity;
    let bottom = -Infinity;

    elements.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      left = Math.min(left, rect.left);
      top = Math.min(top, rect.top);
      right = Math.max(right, rect.right);
      bottom = Math.max(bottom, rect.bottom);
    });

    if (left === Infinity) return null;
    return { left, top, width: right - left, height: bottom - top };
  }

  /**
   * Map a viewport rect onto the source image, undoing `object-fit: cover` with
   * `object-position: center center`. Returns normalised 0-1 coordinates.
   */
  function regionInImage(img, rect) {
    const box = img.getBoundingClientRect();
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    if (!box.width || !box.height || !nw || !nh) return null;

    const scale = Math.max(box.width / nw, box.height / nh);
    const drawnWidth = nw * scale;
    const drawnHeight = nh * scale;
    const originX = box.left + (box.width - drawnWidth) / 2;
    const originY = box.top + (box.height - drawnHeight) / 2;

    const x = clamp01((rect.left - originX) / drawnWidth);
    const y = clamp01((rect.top - originY) / drawnHeight);
    const w = clamp01((rect.left + rect.width - originX) / drawnWidth) - x;
    const h = clamp01((rect.top + rect.height - originY) / drawnHeight) - y;
    if (w <= 0 || h <= 0) return null;

    return { x, y, w, h };
  }

  const probeCache = new Map();

  function loadProbe(src) {
    if (probeCache.has(src)) return probeCache.get(src);

    const promise = new Promise((resolve, reject) => {
      const probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.decoding = 'async';
      probe.onload = () => resolve(probe);
      probe.onerror = reject;
      probe.src = probeSrc(src);
    });

    probeCache.set(src, promise);
    return promise;
  }

  /** Average relative luminance (0-1) of a normalised region of the image. */
  async function measure(src, region) {
    const probe = await loadProbe(src);
    const nw = probe.naturalWidth;
    const nh = probe.naturalHeight;
    if (!nw || !nh) return null;

    const sx = region.x * nw;
    const sy = region.y * nh;
    const sw = Math.max(1, region.w * nw);
    const sh = Math.max(1, region.h * nh);

    const canvas = document.createElement('canvas');
    canvas.width = SAMPLE_SIZE;
    canvas.height = SAMPLE_SIZE;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(probe, sx, sy, sw, sh, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);

    // Throws on a tainted canvas — the caller leaves the authored colours alone.
    const { data } = ctx.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
    let sum = 0;
    for (let i = 0; i < data.length; i += 4) {
      sum += 0.2126 * toLinear(data[i]) + 0.7152 * toLinear(data[i + 1]) + 0.0722 * toLinear(data[i + 2]);
    }
    return sum / (data.length / 4);
  }

  function paint(hero, isLight) {
    const foreground = isLight ? '#000000' : '#ffffff';
    const background = isLight ? '#ffffff' : '#000000';
    const foregroundRgb = isLight ? '0, 0, 0' : '255, 255, 255';
    const tint = isLight ? '255 255 255' : '0 0 0';

    hero.dataset.heroContrast = isLight ? 'light' : 'dark';

    const content = hero.querySelector('.hero__content-wrapper');
    if (content) {
      content.querySelectorAll('.text-block').forEach((el) => {
        el.style.setProperty('--color', foreground);
        el.style.setProperty('--color-rgb', foregroundRgb);
      });

      content.querySelectorAll('.button, .button-custom').forEach((el) => {
        el.style.setProperty('--button-background-color', foreground);
        el.style.setProperty('--button-color', background);
        el.style.setProperty('--button-border-color', foreground);
      });
    }

    hero.querySelectorAll('.hero__media-grid .overlay').forEach((el) => {
      const alpha = overlayAlpha(el.style.getPropertyValue('--overlay-color'));
      el.style.setProperty('--overlay-color', `rgb(${tint} / ${alpha})`);
      el.style.setProperty('--overlay-color--end', `rgb(${tint} / 0)`);
    });
  }

  /**
   * A transparent header floats above the hero, so its icons and logo need their
   * own reading of the strip of image sitting directly behind it.
   */
  function paintHeader(header, isLight) {
    header.dataset.heroContrast = isLight ? 'light' : 'dark';
    header.style.setProperty('--color-transparent-text', isLight ? '#000000' : '#ffffff');
    header.style.setProperty('--color-transparent-text-rgb', isLight ? '0 0 0' : '255 255 255');
    header.style.setProperty('--color-transparent-text-contrast', isLight ? '#ffffff' : '#000000');
  }

  async function updateHeader(img, src) {
    const header = document.querySelector('header-component[transparent], .header[transparent]');
    if (!header) return;

    const headerRect = header.getBoundingClientRect();
    const imgRect = img.getBoundingClientRect();
    const left = Math.max(headerRect.left, imgRect.left);
    const right = Math.min(headerRect.right, imgRect.right);
    const top = Math.max(headerRect.top, imgRect.top);
    const bottom = Math.min(headerRect.bottom, imgRect.bottom);
    // Only when the header actually overlaps this hero's image.
    if (right - left < 4 || bottom - top < 4) return;

    const region = regionInImage(img, { left, top, width: right - left, height: bottom - top });
    if (!region) return;

    const luminance = await measure(src, region);
    if (luminance === null) return;
    paintHeader(header, luminance > LIGHT_THRESHOLD);
  }

  /** The visible hero image — mobile and desktop wrappers are toggled with display. */
  function visibleImage(hero) {
    const images = hero.querySelectorAll('.hero__media-grid img.hero__media');
    for (const img of images) {
      const wrapper = img.closest('.hero__media-wrapper');
      if (wrapper && getComputedStyle(wrapper).display === 'none') continue;
      if (img.getBoundingClientRect().width > 0) return img;
    }
    return null;
  }

  async function update(hero) {
    const img = visibleImage(hero);
    const content = hero.querySelector('.hero__content-wrapper');
    if (!img || !content) return;

    const blocks = Array.from(content.children).filter((el) => el.getBoundingClientRect().height > 0);
    const rect = unionRect(blocks.length ? blocks : [content]);
    if (!rect) return;

    const region = regionInImage(img, rect);
    if (!region) return;

    const src = img.currentSrc || img.src;
    if (!src) return;

    // Hero content and header are measured separately — one failing must not block the other.
    try {
      const luminance = await measure(src, region);
      if (luminance !== null) paint(hero, luminance > LIGHT_THRESHOLD);
    } catch {
      // Cross-origin image without CORS, decode failure, etc. — keep the authored colours.
    }

    try {
      await updateHeader(img, src);
    } catch {
      // Same here: leave the header's configured transparent colour alone.
    }
  }

  function schedule(hero) {
    const img = visibleImage(hero);
    if (img && !img.complete) {
      img.addEventListener('load', () => update(hero), { once: true });
      return;
    }
    update(hero);
  }

  function init(root = document) {
    root.querySelectorAll('.hero[data-hero-auto-contrast]').forEach(schedule);
  }

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => init(), 200);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  // Theme editor: re-run when a section is re-rendered.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
